import { WORLD_RADIUS } from "../constants";
import type { Vec2 } from "../math";

/**
 * A sphere (a world) is split into square areas, each simulated on its own
 * (one Durable Object each on Cloudflare). Area (0, 0) is centred on the
 * middle of the sphere, where the home asteroid is; area (ax, ay) covers
 * [ax * size - size/2, (ax + 1) * size - size/2) on each axis.
 *
 * A small sphere can be one area: give it an `areaSize` wider than the
 * sphere, and there are no borders at all.
 */
export interface SphereConfig {
  /** "public", or a private sphere's id. Part of every area's name. */
  id: string;
  seed: number;
  /** The shell's radius, in blocks. */
  radius: number;
  /** Side of one area, in blocks. */
  areaSize: number;
}

export const AREA = {
  /**
   * The strip each side of a border where a player is connected to both
   * areas (to every area within this of them) and each area sends the other
   * ghosts of what's in it. At least the biggest ship's half-length plus
   * what a player can see past the line.
   */
  margin: 400,
  /** How far past the line something's centre goes before it changes owner, so it doesn't flip back and forth. */
  hysteresis: 24,
  /** Extra distance before a player drops the socket to an area they've left the margin of. */
  dropHysteresis: 120,
  /**
   * World generation places things in cells about this big: each a star's
   * system, with the star at its middle, or a dark reach (see generateCell).
   * As many of them along an area's side as fit nearest this size.
   */
  cellTarget: 4400,
} as const;

/** The public sphere: one big shared world. */
export const PUBLIC_SPHERE = { id: "public", radius: 12_800, areaSize: 4_400 } as const;

/** A sphere that's one area (no borders): today's world size by default. */
export function singleAreaSphere(id: string, seed: number, radius: number = WORLD_RADIUS): SphereConfig {
  return { id, seed, radius, areaSize: Math.ceil(radius * 2 + 2) };
}

export interface AreaCoord {
  x: number;
  y: number;
}

/** An area's short name within its sphere ("2,-1"). */
export const areaKey = (a: AreaCoord): string => `${a.x},${a.y}`;

/** The area a short name stands for. */
export function parseAreaKey(key: string): AreaCoord | null {
  const m = /^(-?\d+),(-?\d+)$/.exec(key);
  return m ? { x: Number(m[1]), y: Number(m[2]) } : null;
}

/** The name an area's Durable Object goes by: "<sphere>:<x>:<y>". */
export const areaName = (sphere: string, a: AreaCoord): string => `${sphere}:${a.x}:${a.y}`;

/** The area a world point is in. */
export function areaOf(sphere: SphereConfig, p: Vec2): AreaCoord {
  const s = sphere.areaSize;
  return { x: Math.floor((p.x + s / 2) / s), y: Math.floor((p.y + s / 2) / s) };
}

export interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** What an area covers: [x0, x1) by [y0, y1). */
export function areaBounds(sphere: SphereConfig, a: AreaCoord): Bounds {
  const s = sphere.areaSize;
  return { x0: a.x * s - s / 2, y0: a.y * s - s / 2, x1: (a.x + 1) * s - s / 2, y1: (a.y + 1) * s - s / 2 };
}

/** How far `p` is outside `b` (0 inside). */
export function distanceOutside(b: Bounds, p: Vec2): number {
  const dx = Math.max(b.x0 - p.x, 0, p.x - b.x1);
  const dy = Math.max(b.y0 - p.y, 0, p.y - b.y1);
  return Math.hypot(dx, dy);
}

/** Is any of the area inside the sphere? (Areas wholly outside its shell don't exist.) */
export function areaExists(sphere: SphereConfig, a: AreaCoord): boolean {
  return distanceOutside(areaBounds(sphere, a), { x: 0, y: 0 }) < sphere.radius;
}

/** Is `a` this area? */
export const sameArea = (a: AreaCoord, b: AreaCoord): boolean => a.x === b.x && a.y === b.y;

/** The (up to 8) areas round `a` that exist. */
export function neighbourAreas(sphere: SphereConfig, a: AreaCoord): AreaCoord[] {
  const out: AreaCoord[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const n = { x: a.x + dx, y: a.y + dy };
      if ((dx || dy) && areaExists(sphere, n)) out.push(n);
    }
  }
  return out;
}

/** Every existing area within `reach` of `p` (its own area first). */
export function areasNear(sphere: SphereConfig, p: Vec2, reach: number): AreaCoord[] {
  const own = areaOf(sphere, p);
  return [own, ...neighbourAreas(sphere, own).filter((a) => distanceOutside(areaBounds(sphere, a), p) < reach)];
}

/** Every area of a sphere. */
export function allAreas(sphere: SphereConfig): AreaCoord[] {
  const n = Math.ceil(sphere.radius / sphere.areaSize) + 1;
  const out: AreaCoord[] = [];
  for (let y = -n; y <= n; y++) for (let x = -n; x <= n; x++) if (areaExists(sphere, { x, y })) out.push({ x, y });
  return out;
}

/**
 * Should something whose centre is at `p`, owned by area `a`, move to
 * another area? Only once it's `AREA.hysteresis` past the line. Returns the
 * area it belongs in now, or null to stay.
 */
export function ownerAfterMove(sphere: SphereConfig, a: AreaCoord, p: Vec2): AreaCoord | null {
  if (distanceOutside(areaBounds(sphere, a), p) <= AREA.hysteresis) return null;
  const next = areaOf(sphere, p);
  return sameArea(next, a) ? null : next;
}

// ------------------------------------------------------------------ cells

/** How many world-generation cells fit along one side of an area (see AREA.cellTarget). */
export const cellsPerArea = (sphere: SphereConfig): number => Math.max(1, Math.round(sphere.areaSize / AREA.cellTarget));

/** The middle of a cell: where its sun is, if it has one. */
export function cellCentre(sphere: SphereConfig, cx: number, cy: number): Vec2 {
  const b = cellBounds(sphere, cx, cy);
  return { x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 };
}

/** Side of one world-generation cell. */
export const cellSize = (sphere: SphereConfig): number => sphere.areaSize / cellsPerArea(sphere);

/** What a cell covers (cells line up with areas: area (ax, ay) is cells ax*n .. ax*n + n-1 each way). */
export function cellBounds(sphere: SphereConfig, cx: number, cy: number): Bounds {
  const c = cellSize(sphere);
  const o = -sphere.areaSize / 2;
  return { x0: o + cx * c, y0: o + cy * c, x1: o + (cx + 1) * c, y1: o + (cy + 1) * c };
}

/** The cells of an area. */
export function cellsOfArea(sphere: SphereConfig, a: AreaCoord): [number, number][] {
  const n = cellsPerArea(sphere);
  const out: [number, number][] = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) out.push([a.x * n + x, a.y * n + y]);
  return out;
}

/** The area a cell is in. */
export function areaOfCell(sphere: SphereConfig, cx: number, cy: number): AreaCoord {
  const n = cellsPerArea(sphere);
  return { x: Math.floor(cx / n), y: Math.floor(cy / n) };
}
